'use client';

import type { User } from 'firebase/auth';
import { onAuthStateChanged } from 'firebase/auth';
import type { ReactNode } from 'react';
// eslint-disable-next-line react/no-use-context -- useContext is more stable for SSR/hydration than use()
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useStore } from 'zustand';

import { getActivityListService } from '@/services/httpServices/ActivityServices';
import { resolveAuthToken } from '@/services/httpServices/authTokenResolver';
import {
  AppSyncEventsService,
  getActivityChannel,
  WEBSOCKET_URLS,
} from '@/services/websocket';
import type { ActivityData, ActivityUpdatedEvent, WebSocketEvent } from '@/types/websocket';
import { WebSocketConnectionState } from '@/types/websocket';
import { auth } from '@/utils/firebase-helpers';
import { getTargetEnvironmentClient } from '@/utils/target-detection';
import { mapApiActivityToActivityData } from '@/validations/websocket';

import type { WebSocketStoreApi } from './store';
import { createWebSocketStore } from './store';
import type { WebSocketStore } from './types';

// ------------- Helpers -------------

/**
 * Helper to check if effect is still active.
 * Extracted to break TypeScript's control flow narrowing.
 */
function isEffectActive(stateRef: { isActive: boolean }): boolean {
  return stateRef.isActive;
}

// ------------- Context -------------

/**
 * Context for the WebSocket store.
 * Following official Zustand v5 Next.js pattern.
 */
const WebSocketStoreContext = createContext<WebSocketStoreApi | null>(null);

// ------------- Firebase User Hook -------------

/**
 * Hook to get the current Firebase user
 */
function useFirebaseUser(): User | null {
  const [user, setUser] = useState<User | null>(null);

  useEffect(() => {
    let unsubscribe: (() => void) | null = null;

    const initAuth = async () => {
      try {
        const authInstance = await auth();
        unsubscribe = onAuthStateChanged(authInstance, (firebaseUser) => {
          setUser(firebaseUser);
        });
      } catch {
        // Auth initialization failed silently
      }
    };

    initAuth().catch(() => {});

    return () => {
      if (unsubscribe !== null) {
        unsubscribe();
      }
    };
  }, []);

  return user;
}

// ------------- Provider Props -------------

type WebSocketProviderProps = {
  children: ReactNode;
  /** User ID to subscribe to activity channel (optional, will use Firebase user if not provided) */
  userId?: string | null;
  /** Whether WebSocket should be enabled */
  enabled?: boolean;
};

// ------------- Provider -------------

/**
 * Provider for WebSocket store context.
 * Following official Zustand v5 Next.js pattern with per-request store creation.
 *
 * Features:
 * - Initializes WebSocket service when user is authenticated
 * - Subscribes to activity channel for the user
 * - Updates Zustand store on events
 * - Handles reconnection automatically
 * - Cleans up on unmount/logout
 *
 * Usage:
 * ```tsx
 * <WebSocketProvider userId={user?.uid} enabled={true}>
 *   <App />
 * </WebSocketProvider>
 * ```
 */
export function WebSocketProvider({
  children,
  userId: providedUserId,
  enabled = true,
}: WebSocketProviderProps) {
  // Create store once per provider instance (official Zustand v5 pattern)
  const [store] = useState<WebSocketStoreApi>(() => createWebSocketStore());

  // Get Firebase user if no userId provided
  const firebaseUser = useFirebaseUser();
  const userId = providedUserId ?? firebaseUser?.uid ?? null;

  // Keep reference to WebSocket service
  const serviceRef = useRef<AppSyncEventsService | null>(null);
  const subscriptionIdRef = useRef<string | null>(null);

  // Connection state change handler - uses batched action for efficiency
  // Store is stable (created once by useState) so it's safe to use directly
  const handleConnectionStateChange = useCallback((state: WebSocketConnectionState) => {
    const shouldResetAttempts = state === WebSocketConnectionState.CONNECTED;
    const shouldIncrementAttempts = state === WebSocketConnectionState.RECONNECTING;

    store.getState().handleConnectionStateChange(
      state,
      shouldResetAttempts,
      shouldIncrementAttempts,
    );
  }, [store]);

  // Error handler
  const handleError = useCallback((error: Error) => {
    store.getState().setError(error.message);
  }, [store]);

  // Event handler
  const handleEvent = useCallback((event: WebSocketEvent) => {
    if (event.type === 'ActivityUpdated') {
      const activityEvent = event as ActivityUpdatedEvent;
      const activity = activityEvent.data;

      // Add/update activity in store (store handles deduplication by ID)
      store.getState().addActivity(activity);
    }
  }, [store]);

  // Track if effect is active to prevent operations after cleanup
  // Use object with explicit boolean type to avoid TypeScript control flow narrowing
  const effectStateRef = useRef<{ isActive: boolean }>({ isActive: true });

  // Initialize WebSocket service
  useEffect(() => {
    // Mark effect as active
    effectStateRef.current.isActive = true;

    if (enabled === false || userId === null || userId === undefined || userId === '') {
      // Clean up if disabled or no user
      if (serviceRef.current !== null) {
        serviceRef.current.disconnect();
        serviceRef.current = null;
        subscriptionIdRef.current = null;
        store.getState().reset();
      }
      return;
    }

    // Get environment and WebSocket URL
    const target = getTargetEnvironmentClient();
    const wsUrl = WEBSOCKET_URLS[target];

    // Create WebSocket service
    const service = new AppSyncEventsService({
      url: wsUrl,
      getAuthToken: async () => {
        const token = await resolveAuthToken();
        // Return empty string if token is null/undefined to trigger auth error in service
        if (token === null || token === undefined) {
          return '';
        }
        return token;
      },
      onConnectionStateChange: handleConnectionStateChange,
      onError: handleError,
    });

    serviceRef.current = service;

    // Fetch initial activities from API
    const fetchInitialActivities = async () => {
      // Check if effect is still active before proceeding
      if (isEffectActive(effectStateRef.current) === false) {
        return;
      }

      try {
        const token = await resolveAuthToken();
        if (token === null || token === undefined) {
          return;
        }

        // Check if effect is still active after async operation
        if (isEffectActive(effectStateRef.current) === false) {
          return;
        }

        const response = await getActivityListService({
          httpRequestConfig: {
            target: getTargetEnvironmentClient(),
            authToken: token,
            space: '', // Not needed for this endpoint
          },
          params: {
            params: { id: userId },
            query: {
              sortByField: 'startedAt',
              sortByOrder: 'desc',
              paginationFirst: 20, // Fetch last 20 activities
            },
          },
        });

        // Check again before updating state
        if (isEffectActive(effectStateRef.current) === false) {
          return;
        }

        // Map and add activities to store
        const activities: ActivityData[] = [];
        for (const apiActivity of response.nodes) {
          const mapped = mapApiActivityToActivityData(apiActivity);
          if (mapped !== null) {
            activities.push(mapped);
          }
        }

        // Add activities to store (most recent first)
        for (const activity of activities) {
          store.getState().addActivity(activity);
        }
      } catch {
        // Don't fail the connection if initial fetch fails
      }
    };

    // Connect and subscribe
    const connectAndSubscribe = async () => {
      // Check if effect is still active before proceeding
      if (isEffectActive(effectStateRef.current) === false) {
        return;
      }

      try {
        // Fetch initial activities first
        await fetchInitialActivities();

        // Check if effect is still active after async operation
        if (isEffectActive(effectStateRef.current) === false) {
          return;
        }

        // Mark as hydrated after initial fetch completes
        store.getState().setHydrated(true);

        const channel = getActivityChannel(userId);
        const subId = await service.subscribe({
          channel,
          onEvent: handleEvent,
          onError: handleError,
        });

        // Only store subscription ID if still active
        if (isEffectActive(effectStateRef.current) === true) {
          subscriptionIdRef.current = subId;
        }
      } catch (error) {
        // Only handle error if still active
        if (isEffectActive(effectStateRef.current) === true) {
          handleError(error instanceof Error ? error : new Error(String(error)));
          // Still mark as hydrated even on error so UI doesn't show loading forever
          store.getState().setHydrated(true);
        }
      }
    };

    connectAndSubscribe().catch(() => {});

    // Cleanup on unmount or dependency change
    return () => {
      effectStateRef.current.isActive = false;
      if (serviceRef.current !== null) {
        serviceRef.current.disconnect();
        serviceRef.current = null;
        subscriptionIdRef.current = null;
      }
    };
  }, [enabled, userId, handleConnectionStateChange, handleError, handleEvent, store]);

  return (
    <WebSocketStoreContext value={store}>
      {children}
    </WebSocketStoreContext>
  );
}

// ------------- Hook to Access Store -------------

/**
 * Hook to select state from the WebSocket store.
 * Following official Zustand v5 Next.js pattern.
 *
 * @throws Error if used outside of WebSocketProvider
 *
 * @example
 * ```tsx
 * const connectionState = useWebSocketStore(state => state.connectionState);
 * ```
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useWebSocketStore<T>(selector: (state: WebSocketStore) => T): T {
  // eslint-disable-next-line react/no-use-context -- useContext is more stable for SSR/hydration
  const store = useContext(WebSocketStoreContext);

  if (store === null) {
    throw new Error('useWebSocketStore must be used within WebSocketProvider');
  }

  return useStore(store, selector);
}

// ------------- Direct Store Access -------------

/**
 * Hook to access the raw store API.
 * Useful for imperative operations outside of React render.
 *
 * @throws Error if used outside of WebSocketProvider
 *
 * @example
 * ```tsx
 * const storeApi = useWebSocketStoreApi();
 * // Later, in an event handler:
 * storeApi.getState().addActivity(activity);
 * ```
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useWebSocketStoreApi(): WebSocketStoreApi {
  // eslint-disable-next-line react/no-use-context -- useContext is more stable for SSR/hydration
  const store = useContext(WebSocketStoreContext);

  if (store === null) {
    throw new Error('useWebSocketStoreApi must be used within WebSocketProvider');
  }

  return store;
}
